import {
  Alert,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { useCapabilityMap } from "../../hooks/queries/useCapabilityMap";
import { matrixQuery } from "../../hooks/queries/useMatrix";
import { useProductSearch } from "../../hooks/queries/useProductSearch";
import {
  ALL_FILTER_VALUES,
  ExploreNarrow,
  PRODUCT_NOUN,
  exploreActiveFilters,
  noFilterNames,
  rememberFilterNames,
  unnamedLabel,
} from "../Explore/exploreFilters";
import {
  NO_EXPLORE_FILTERS,
  toProductSearchParams,
} from "../Explore/exploreState";
import { exportFilename } from "../../utils/export/filename";
import ExportFrame from "../../utils/export/ExportFrame";
import { useExportStage } from "../../utils/export/ExportStage";
import { tableExportOptions } from "../../utils/export/table";
import ExportMenu, { ExportOption } from "../common/ExportMenu";
import { ActiveFilter } from "../Filters/ActiveFilterChips";
import EdgeDetailOverlay from "../Layout/EdgeDetailOverlay";
import EntityDetailOverlay from "../Layout/EntityDetailOverlay";
import { useEntityOverlay } from "../Layout/useEntityOverlay";
import ScopeBar from "./ScopeBar";
import ScopeFilters from "./ScopeFilters";
import {
  ANALYTICS_VIEW_LABELS,
  AnalyticsSelection,
  AnalyticsView as View,
  SHOWN_ANALYTICS_VIEWS,
  exploreAddress,
  scopeQueryOf,
  selectionOf,
  toCapabilityMapParams,
  toExploreState,
  toMatrixParams,
} from "./analyticsState";
import { matrixExport } from "./Matrix/matrixModel";
import { PANES } from "./analyticsPanes";
import { rememberScope } from "./scopeMemory";
import { useAnalyticsState } from "./useAnalyticsState";

// A part of the selection as the scope bar shows it: its chip, and the
// change of the selection that takes it away
interface ScopeChip extends ActiveFilter {
  remove: Partial<AnalyticsSelection>;
}

// The change that takes the whole selection away: every product again
const NO_SELECTION: Partial<AnalyticsSelection> = {
  ...NO_EXPLORE_FILTERS,
  keyword: "",
  unnamed: false,
};

// The analytics page: lenses over a selection of products. The selection is
// written as Explore writes it, carried in the address and changed on the
// page itself, in the dropdown of the scope bar; the page states it, its
// totals and the sources behind them, and offers the views. What it shows is
// held in the address (see analyticsState).
const AnalyticsView: React.FC = () => {
  const [state, change] = useAnalyticsState();
  const {
    detail,
    open,
    close,
    relationshipId,
    closeRelationship,
    handlePageClick,
  } = useEntityOverlay();

  // The scope's totals come with the capability map's read, the evidence
  // block every analytics read carries
  const read = useCapabilityMap(useMemo(() => toCapabilityMapParams(state), [state]));
  const evidence = read.data?.evidence ?? null;

  // What the filters of the dropdown ask for beside the selection. Neither
  // is part of the address: the text typed into the box of one long filter,
  // and whether a cut list was asked for in whole.
  const [narrow, setNarrow] = useState<ExploreNarrow | null>(null);
  const [allFilterValues, setAllFilterValues] = useState(false);

  // The filters, each value with its count, and the names of the values for
  // the chips come from the product search of the same selection: the
  // request Explore itself makes, so that arriving from Explore answers from
  // its cache
  const selection = useMemo(() => selectionOf(state), [state]);
  const exploreState = useMemo(() => toExploreState(selection), [selection]);
  const search = useProductSearch(
    useMemo(
      () =>
        toProductSearchParams(exploreState, {
          filterLimit: allFilterValues ? ALL_FILTER_VALUES : undefined,
          narrow,
        }),
      [exploreState, narrow, allFilterValues],
    ),
  );
  const filterNames = useRef(noFilterNames());
  const chips = useMemo((): ScopeChip[] => {
    if (search.data?.filters) {
      rememberFilterNames(filterNames.current, search.data.filters);
    }
    for (const node of read.data?.nodes ?? []) {
      for (const ref of [node.capability, node.core, node.qualifier]) {
        if (ref?.name) {
          for (const name of ["capability", "mission", "condition", "mode"] as const) {
            const labels = filterNames.current.values[name] ?? new Map();
            filterNames.current.values[name] = labels;
            if (!labels.has(ref.id)) {
              labels.set(ref.id, ref.name);
            }
          }
        }
      }
    }
    // The keyword first, then the picks and the ranges as Explore shows
    // them, then the switch: each narrows or widens the scope
    return [
      ...(exploreState.keyword
        ? [
            {
              id: "keyword",
              section: "Keyword",
              value: exploreState.keyword,
              remove: { keyword: "" },
            },
          ]
        : []),
      ...exploreActiveFilters(exploreState, filterNames.current),
      ...(exploreState.unnamed
        ? [
            {
              id: "unnamed",
              section: unnamedLabel(PRODUCT_NOUN),
              value: "included",
              remove: { unnamed: false },
            },
          ]
        : []),
    ];
  }, [exploreState, search.data, read.data]);

  // The last scope of the session, for the sidebar entry to reopen
  const scopeQuery = useMemo(() => scopeQueryOf(selection), [selection]);
  useEffect(() => {
    rememberScope(scopeQuery);
  }, [scopeQuery]);

  const Pane = PANES[state.view];

  // The active pane, drawn again off screen as an image, without its
  // controls (useCapturing), under the view's name and the scope. The copy
  // is read-only: it changes nothing and opens nothing. There is no PDF for
  // now: the panes do not yet lay out on a printed page. The matrix, a
  // table, is also offered as a file of the rows shown, read through the
  // cache its pane reads from.
  const { capture } = useExportStage();
  const queryClient = useQueryClient();
  const exportOptions = useMemo((): ExportOption[] => {
    const view = state.view;
    // The scope as the chips show it, the picks of one filter together
    const grouped = new Map<string, string[]>();
    for (const chip of chips) {
      grouped.set(chip.section, [...(grouped.get(chip.section) ?? []), chip.value]);
    }
    const filters = Array.from(grouped, ([label, values]) => [label, values.join(", ")] as const);
    const framed = () => (
      <ExportFrame
        title={`Analytics: ${ANALYTICS_VIEW_LABELS[view]}`}
        filters={filters}
        generatedAt={new Date()}
      >
        <Pane state={state} onChange={() => {}} onOpenEntity={() => {}} />
      </ExportFrame>
    );
    const image = (
      mode: "light" | "dark",
      format: "png" | "svg",
    ): ExportOption => ({
      key: `${format}-${mode}`,
      label: `${format.toUpperCase()} (${mode})`,
      run: () =>
        capture(framed(), {
          mode,
          format,
          // Light is the default and is not named; dark is, so the two differ
          filename: exportFilename(
            ["analytics", view, mode === "dark" ? mode : null],
            format,
          ),
        }),
    });
    const images = [image("light", "png"), image("dark", "png"), image("light", "svg")];
    if (view !== "matrix") {
      return images;
    }
    const tables = tableExportOptions(async () =>
      matrixExport(
        await queryClient.fetchQuery(matrixQuery(toMatrixParams(state))),
        state.settings.matrix.find,
        filters,
      ),
    );
    return [...tables, ...images];
  }, [Pane, state, chips, capture, queryClient]);

  return (
    <Box
      onClick={handlePageClick}
      sx={{
        height: "100%",
        overflow: "auto",
        px: 3,
        pt: "22px",
        pb: 5,
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        backgroundColor: "background.default",
      }}
    >
      {/* The title and the views */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: 3,
          rowGap: 1,
        }}
        component="header"
      >
        <Typography variant="h5" component="h1">
          Analytics
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={state.view}
          aria-label="Analytics view"
          onChange={(_event, next: View | null) => {
            // A click on the chosen view asks for none: keep the view
            if (next) {
              change({ view: next });
            }
          }}
        >
          {SHOWN_ANALYTICS_VIEWS.map((view) => (
            <ToggleButton
              key={view}
              value={view}
              sx={{ height: 36, px: 1.5, fontSize: 13, fontWeight: 500 }}
            >
              {ANALYTICS_VIEW_LABELS[view]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ ml: "auto" }}>
          <ExportMenu options={exportOptions} />
        </Box>
      </Box>

      <ScopeBar
        filters={chips}
        onRemove={(id) => {
          const removed = chips.find((chip) => chip.id === id);
          if (removed) {
            change(removed.remove);
          }
        }}
        onClearAll={() => change(NO_SELECTION)}
        evidence={evidence}
        floors={read.data?.floors ?? null}
        thinScope={read.data?.thin_scope ?? false}
        exploreHref={exploreAddress(selection)}
      >
        <ScopeFilters
          selection={selection}
          onChange={change}
          filters={search.data?.filters ?? null}
          failed={Boolean(search.error)}
          narrow={narrow}
          onNarrowChange={setNarrow}
          onShowAll={() => setAllFilterValues(true)}
        />
      </ScopeBar>

      {read.error && (
        <Alert severity="error">The analytics could not be loaded.</Alert>
      )}

      <Pane state={state} onChange={change} onOpenEntity={open} />

      {/* The detail the address names, beside the pane, which stays */}
      {detail && (
        <EntityDetailOverlay
          entityId={detail.entityId}
          entityType={detail.entityType}
          onClose={close}
          onEntityClick={open}
        />
      )}

      {/* The relationship opened from an entity's relation table */}
      {relationshipId && (
        <EdgeDetailOverlay
          relationshipId={relationshipId}
          onClose={closeRelationship}
          onEntityClick={open}
        />
      )}
    </Box>
  );
};

export default AnalyticsView;
